/**
 * index.tsx
 * Application Entry Point
 */

import { ThemeProvider } from '@material-ui/core/styles';
import { App } from 'app';
import { AuditProvider } from 'contexts/audit/admin/Audit';
import { ConciliationRequestAdminProvider } from 'contexts/conciliation/admin/Conciliation';
import { ConciliationProvider } from 'contexts/conciliation/Conciliation';
import { LibraryProvider } from 'contexts/library/Library';
import { NotificationProvider } from 'contexts/notification/admin/Notification';
import FontFaceObserver from 'fontfaceobserver';
import * as React from 'react';
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import * as ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import reportWebVitals from 'reportWebVitals';
// Use consistent styling
import 'sanitize.css/sanitize.css';
import { configureAppStore } from 'store/configureStore';
import { ConfigParamProvider } from './contexts/config/admin/ConfigParam';
import { UserAdminProvider } from './contexts/user/admin/User';
import { UserProvider } from './contexts/user/User';
// Initialize languages
import './locales/i18n';
import theme from './styles/simple/theme';
import { EthicalInboxProvider } from 'contexts/ethical-inbox/EthicalInbox';
import { EthicalInboxAdminProvider } from 'contexts/ethical-inbox/admin/EthicalInbox';

// Observe loading of Inter (to remove 'Inter', remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Inter', {});

// When Inter is loaded, add a font-family using Inter to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
});

export const appReduxStore = configureAppStore();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;

ReactDOM.render(
  <Provider store={appReduxStore}>
    <ThemeProvider theme={theme}>
      <HelmetProvider>
        <React.StrictMode>
          <ConfigParamProvider>
            <AuditProvider>
              <NotificationProvider>
                <LibraryProvider>
                  <UserAdminProvider>
                    <UserProvider>
                      <ConciliationProvider>
                        <ConciliationRequestAdminProvider>
                          <EthicalInboxProvider>
                            <EthicalInboxAdminProvider>
                              <App />
                            </EthicalInboxAdminProvider>
                          </EthicalInboxProvider>
                        </ConciliationRequestAdminProvider>
                      </ConciliationProvider>
                    </UserProvider>
                  </UserAdminProvider>
                </LibraryProvider>
              </NotificationProvider>
            </AuditProvider>
          </ConfigParamProvider>
        </React.StrictMode>
      </HelmetProvider>
    </ThemeProvider>
  </Provider>,
  MOUNT_NODE,
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
